import { useState, useCallback, useMemo, useEffect, useRef } from "react";
import { TYPOGRAPHY, FORM_CONTROLS, INTERACTION_STATES } from '@/design-system';
import { Button } from "@/components/ui/button";
import { Check, ChevronDown, Languages } from "lucide-react";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { useLocale } from "@/contexts/LocaleContext";
import { armDomainRedirect } from "@/lib/domainRedirectArm";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import i18n from "@/i18n/config";
import { useLocalizations } from "@/hooks/useLocalizations";
import { useLocalization } from "@/hooks/useLocalization";
import { useLocalizedLanguages } from "@/hooks/useLocalizedLanguages";
import { useLocaleConfigs } from "@/hooks/useLocaleConfigs";
import { createSearchFilter } from "@/lib/commandUtils";
import { resolveCanonicalLocale } from "@/lib/localeResolver";
import { LOCALIZATION_NAMESPACES } from "@/i18n/namespaceConfig";


interface LanguageSelectorProps {
  variant?: 'default' | 'footer';
}

export const LanguageSelector = ({ variant = 'default' }: LanguageSelectorProps) => {
  const { settings } = useLocale();
  const { changeLanguage } = useLocalization();
  const { lCommon } = useLocalizations();
  const { languages, isLoading, getLocalizedName } = useLocalizedLanguages();
  const { data: localeConfigs } = useLocaleConfigs();
  
  // Reactive i18n language state - source of truth for actual UI language
  const [i18nLanguage, setI18nLanguage] = useState(i18n.language);
  
  useEffect(() => {
    const handleLanguageChange = (lng: string) => {
      setI18nLanguage(lng);
    };
    
    i18n.on('languageChanged', handleLanguageChange);
    return () => {
      i18n.off('languageChanged', handleLanguageChange);
    };
  }, []);
  
  // locale-logic: Extract base language for display grouping only (not for content resolution)
  const currentLocale = i18nLanguage?.split('-')[0] || 'en';
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const searchFilter = useMemo(() => createSearchFilter(), []);
  const preloadedLocales = useRef<Set<string>>(new Set());
  const preloadingLocales = useRef<Set<string>>(new Set());
  const listRef = useRef<HTMLDivElement>(null);
  
  // Get supported locales from DB
  const supportedLngs = useMemo(() => 
    localeConfigs?.map(c => c.locale) || [], 
    [localeConfigs]
  );

  const filteredLanguages = languages;

  // Preload all namespaces for a locale by fetching JSON directly and adding to i18n store
  const preloadLocale = useCallback(async (locale: string) => {
    // Skip if already preloaded or currently loading
    if (preloadedLocales.current.has(locale) || preloadingLocales.current.has(locale)) return;
    preloadingLocales.current.add(locale);
    
    // Use base URL for correct asset paths (handles non-root deployments)
    const baseUrl = import.meta.env.BASE_URL || '/';
    
    try {
      // Fetch all namespace JSONs in parallel
      const fetchPromises = LOCALIZATION_NAMESPACES.map(async (ns) => {
        try {
          const response = await fetch(`${baseUrl}locales/${ns}.${locale}.json`);
          if (response.ok) {
            const data = await response.json();
            // Add directly to i18n store for instant switching
            i18n.addResourceBundle(locale, ns, data, true, true);
          }
        } catch {
          // Silent fail for individual namespaces
        }
      });
      
      await Promise.all(fetchPromises);
      preloadedLocales.current.add(locale);
    } finally {
      preloadingLocales.current.delete(locale);
    }
  }, []);

  // Preload top 5 languages when popover opens
  useEffect(() => {
    if (open && languages.length > 0) {
      const topLanguages = languages.slice(0, 5);
      topLanguages.forEach(lang => preloadLocale(lang.locale));
    }
  }, [open, languages, preloadLocale]);

  // Pre-fetch translations on hover for instant language switching
  const handleLanguageHover = useCallback((locale: string) => {
    preloadLocale(locale);
  }, [preloadLocale]);

  const handleLanguageChange = async (languageCode: string) => {
    try {
      const language = filteredLanguages.find(l => l.code === languageCode);
      if (language) {
        // Use canonical resolver with DB-loaded supported locales
        // This ensures we always pick a locale that exists in the DB
        // E.g., "de" -> "de-DE" if DB only has regional locales
        const canonicalLocale = supportedLngs.length > 0
          ? resolveCanonicalLocale(language.code, supportedLngs, navigator.language)
          : language.locale;
        
        // Debug log to help diagnose language switching issues
        if (typeof window !== 'undefined' && localStorage.getItem('i18n_debug') === 'true') {
          console.info('[LanguageSelector] Changing language:', {
            selectedCode: language.code,
            canonicalLocale,
            supportedLngsCount: supportedLngs.length,
            browserLocale: navigator.language,
          });
        }
        
        armDomainRedirect();
        await changeLanguage(canonicalLocale, language.english);
        toast.success(lCommon('toast.language_changed.success', { language: language.native, defaultValue: 'Language changed to {{language}}.' }));
        
        // Close popover after successful selection
        setOpen(false);
        
        // Scroll to top to show selected language on next open
        if (listRef.current) {
          listRef.current.scrollTop = 0;
        }
      }
    } catch (error) {
      toast.error(lCommon('toast.failed_to_change_language.error', { defaultValue: 'Failed to change language.' }));
    }
  };

  // Find current language - use i18n.language as source of truth
  // This ensures the selector always reflects the actual UI language
  const currentLanguage = useMemo(() => {
    if (!languages.length) return undefined;
    
    // Use reactive i18n language - not settings.locale which may lag behind
    const activeLocale = i18nLanguage;
    if (!activeLocale) return languages[0];
    
    // 1. Exact match on locale (e.g., 'es-ES' === 'es-ES')
    const exactMatch = languages.find((l) => l.locale === activeLocale);
    if (exactMatch) return exactMatch;
    
    // 2. Match by code (handles 'es-ES' → 'es' matching)
    // locale-logic: Extract base language for display selector only (not content)
    const baseCode = activeLocale.split('-')[0];
    const codeMatch = languages.find((l) => l.code === baseCode);
    if (codeMatch) return codeMatch;
    
    // 3. Match by language property (fallback)
    const langMatch = languages.find((l) => l.language === baseCode);
    if (langMatch) return langMatch;
    
    // 4. Fallback to English, then first language
    return languages.find((l) => l.code === 'en') || languages[0];
  }, [languages, i18nLanguage]);


  if (isLoading || !currentLanguage) {
    return (
      <Button variant="ghost" size="sm" className={cn(TYPOGRAPHY.cardDescription, 'justify-center')} disabled>
        <span>...</span>
      </Button>
    );
  }

  return (
    <Popover open={open} onOpenChange={(isOpen) => {
      setOpen(isOpen);
      if (!isOpen) setSearch('');
    }}>
      <PopoverTrigger asChild>
        <Button 
          variant="ghost" 
          size="sm" 
          role="combobox"
          aria-expanded={open}
          aria-label={lCommon('aria.select_language', { defaultValue: 'Select Language' })}
          className={cn(
            TYPOGRAPHY.filterTrigger,
            INTERACTION_STATES.navButton.interactive,
            'px-2 sm:px-3 max-w-[180px] sm:max-w-none',
            variant === 'footer' && 'text-muted-foreground'
          )}
        >
          <Languages className="h-3.5 w-3.5 mr-1.5 opacity-60 shrink-0" />
          <span className="truncate">
            {currentLanguage?.native}
          </span>
          <ChevronDown className="h-4 w-4 opacity-50 flex-shrink-0 ml-auto" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className={FORM_CONTROLS.command.popoverWide}>
        <Command filter={searchFilter}>
          <CommandInput 
            placeholder={lCommon('search.languages.placeholder', { defaultValue: 'Search languages' })} 
            value={search}
            onValueChange={setSearch}
          />
          <CommandList ref={listRef}>
            <CommandEmpty>{lCommon('empty.no_language_found', { defaultValue: 'No languages found.' })}</CommandEmpty>
            <CommandGroup>
              {/* Selected language at top when not searching */}
              {!search && currentLanguage && (
                <>
                  <CommandItem
                    key={`selected-${currentLanguage.code}`}
                    value={currentLanguage.native}
                    keywords={[currentLanguage.native, currentLanguage.english, currentLanguage.code, currentLanguage.locale]}
                    onSelect={() => handleLanguageChange(currentLanguage.code)}
                    onMouseEnter={() => handleLanguageHover(currentLanguage.locale)}
                    className={cn("cursor-pointer hover:bg-muted bg-muted")}
                  >
                    <span className="truncate flex-1">
                      {(() => {
                        const translatedName = getLocalizedName(currentLanguage.code, currentLocale);
                        return currentLanguage.native === translatedName
                          ? currentLanguage.native
                          : `${currentLanguage.native} (${translatedName})`;
                      })()}
                    </span>
                    <span className="ml-auto flex-shrink-0 bg-success rounded-full p-0.5">
                      <Check className="h-3 w-3 text-success-foreground" strokeWidth={3} />
                    </span>
                  </CommandItem>
                  <CommandSeparator className="my-1" />
                </>
              )}
              {/* Remaining languages (exclude selected when not searching) */}
              {filteredLanguages
                .filter(language => !search ? language.code !== currentLanguage?.code : true)
                .map((language) => {
                  const isSelected = currentLanguage?.code === language.code;
                  return (
                    <CommandItem
                      key={language.code}
                      value={language.native}
                      keywords={[language.native, language.english, language.code, language.locale]}
                      onSelect={() => handleLanguageChange(language.code)}
                      onMouseEnter={() => handleLanguageHover(language.locale)}
                      className={cn("cursor-pointer hover:bg-muted", isSelected && "bg-muted")}
                    >
                      <span className="truncate flex-1">
                        {(() => {
                          const translatedName = getLocalizedName(language.code, currentLocale);
                          return language.native === translatedName
                            ? language.native
                            : `${language.native} (${translatedName})`;
                        })()}
                      </span>
                      {isSelected && (
                        <span className="ml-auto flex-shrink-0 bg-success rounded-full p-0.5">
                          <Check className="h-3 w-3 text-success-foreground" strokeWidth={3} />
                        </span>
                      )}
                    </CommandItem>
                  );
                })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};
